import {
  Button,
  Table as ChakraTable,
  Flex,
  Input,
  InputGroup,
  InputRightElement,
  Select,
  TableContainer,
  Tbody,
  Td,
  Text,
  Th,
  Thead,
  Tr,
  Badge,
  AlertDialog,
  AlertDialogBody,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogContent,
  AlertDialogOverlay,
  useDisclosure,
  HStack,
  VStack,
  Tooltip,
  Icon
} from "@chakra-ui/react";
import { useEffect, useState, useRef } from "react";
import { BsSearch, BsEye } from "react-icons/bs";
import { useSearchParams } from "react-router-dom";
import useOrders, { OrdersFilter } from "./hooks/useOrders";
import { Order, OlaggModule, PermissionEnum } from "@olagg/db-types";
import useRoles from "../../../features/Roles/hooks/useRoles";
import Pagination from "@olagg/ui-kit/Pagination";
import OrderDetailModal from "./OrderModal";

const Actions = ({ order, onRefresh }: { order: Order, onRefresh: () => void }) => {
  const { missingPermission } = useRoles();
  const { refundOrder, retryBonoxOrder, verifyPayment } = useOrders();
  const { isOpen: isRefundOpen, onOpen: onRefundOpen, onClose: onRefundClose } = useDisclosure();
  const { isOpen: isRetryOpen, onOpen: onRetryOpen, onClose: onRetryClose } = useDisclosure();
  const { isOpen: isDetailOpen, onOpen: onDetailOpen, onClose: onDetailClose } = useDisclosure();
  const cancelRef = useRef<HTMLButtonElement>(null);

  const [isRefunding, setIsRefunding] = useState(false);
  const [isRetrying, setIsRetrying] = useState(false);
  const [isVerifyingPayment, setIsVerifyingPayment] = useState(false);

  const handleRefund = async () => {
    setIsRefunding(true);
    try {
      const success = await refundOrder(order.id);
      if (success) {
        onRefundClose();
        onRefresh();
      }
    } finally {
      setIsRefunding(false);
    }
  };

  const handleRetryBonox = async () => {
    setIsRetrying(true);
    try {
      const success = await retryBonoxOrder(order.id);
      if (success) {
        onRetryClose();
        onRefresh();
      }
    } finally {
      setIsRetrying(false);
    }
  };

  const handleVerifyPayment = async () => {
    setIsVerifyingPayment(true);
    try {
      const success = await verifyPayment(order.id);
      if (success) {
        onRefresh();
      }
    } finally {
      setIsVerifyingPayment(false);
    }
  };

  // Solo mostrar acciones si la orden no está completada
  const showActions = order.status !== 'success' && order.status !== 'refunded';
  const canRefund = order.status === 'for_revision';
  const canRetry = ['error', 'for_revision'].includes(order.status);
  const canVerifyPayment = order.status === 'for_revision' && (order.origin === 'lemon' || order.origin === 'world');

  return (
    <>
      <HStack spacing={2}>
        {/* Botón Ver Detalle - siempre visible */}
        <Tooltip label="Ver detalle completo de la orden">
          <Button
            bgColor={'#4A5568'}
            p='0 8px'
            h='30px'
            fontSize={'12px'}
            onClick={onDetailOpen}
            size="sm"
            _hover={{ bgColor: '#2D3748' }}
          >
            <Icon as={BsEye} />
          </Button>
        </Tooltip>

        {/* Acciones según estado */}
        {showActions && (
          <>
            {canRefund && (
              <Tooltip label="Crear reembolso en USDC para el usuario">
                <Button
                  isDisabled={missingPermission(OlaggModule.Order, PermissionEnum.EDIT)}
                  bgColor={'#E53E3E'}
                  p='0 8px'
                  h='30px'
                  fontSize={'12px'}
                  onClick={onRefundOpen}
                  size="sm"
                  _hover={{ bgColor: '#C53030' }}
                >
                  Reembolsar
                </Button>
              </Tooltip>
            )}

            {canRetry && (
              <Tooltip label="Reintentar compra en API de Bonox">
                <Button
                  isDisabled={missingPermission(OlaggModule.Order, PermissionEnum.EDIT)}
                  bgColor={'#3182CE'}
                  p='0 8px'
                  h='30px'
                  fontSize={'12px'}
                  onClick={onRetryOpen}
                  size="sm"
                  _hover={{ bgColor: '#2C5282' }}
                >
                  Reintentar
                </Button>
              </Tooltip>
            )}

            {canVerifyPayment && (
              <Tooltip label="Verificar si el pago fue procesado">
                <Button
                  isDisabled={missingPermission(OlaggModule.Order, PermissionEnum.EDIT)}
                  bgColor={'#38A169'}
                  p='0 8px'
                  h='30px'
                  fontSize={'12px'}
                  onClick={handleVerifyPayment}
                  isLoading={isVerifyingPayment}
                  loadingText="Verificando..."
                  size="sm"
                  _hover={{ bgColor: '#276749' }}
                >
                  Verificar pago
                </Button>
              </Tooltip>
            )}
          </>
        )}
      </HStack>

      {/* Modal de Detalle */}
      <OrderDetailModal
        order={order}
        isOpen={isDetailOpen}
        onClose={onDetailClose}
      />

      {/* Alert Dialog para Reembolso */}
      <AlertDialog
        isOpen={isRefundOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRefundClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Confirmar Reembolso
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="start" spacing={3}>
                <Text>¿Estás seguro que deseas procesar el reembolso para esta orden?</Text>
                <VStack align="start" spacing={1} w="full" p={3} bg="gray.700" borderRadius="md">
                  <Text fontSize="sm"><strong>Orden ID:</strong> {order.id}</Text>
                  <Text fontSize="sm"><strong>Usuario:</strong> {order.user?.name || order.user?.email}</Text>
                  <Text fontSize="sm"><strong>Producto:</strong> {order.product?.name}</Text>
                  <Text fontSize="sm"><strong>Monto:</strong> ${order.amount} USDC</Text>
                </VStack>
                <Text fontSize="sm" color="yellow.300">
                  Se creará un pago en USDC equivalente al precio de venta para el usuario.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button bgColor={'#3182CE'} ref={cancelRef} onClick={onRefundClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="red"
                onClick={handleRefund}
                ml={3}
                isLoading={isRefunding}
                loadingText="Procesando..."
              >
                Confirmar Reembolso
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>

      {/* Alert Dialog para Reintentar */}
      <AlertDialog
        isOpen={isRetryOpen}
        leastDestructiveRef={cancelRef}
        onClose={onRetryClose}
        isCentered
      >
        <AlertDialogOverlay>
          <AlertDialogContent bg="gray.800" color="white">
            <AlertDialogHeader fontSize="lg" fontWeight="bold">
              Reintentar Compra Bonox
            </AlertDialogHeader>

            <AlertDialogBody>
              <VStack align="start" spacing={3}>
                <Text>¿Deseas reintentar la compra en la API de Bonox?</Text>
                <VStack align="start" spacing={1} w="full" p={3} bg="gray.700" borderRadius="md">
                  <Text fontSize="sm"><strong>Orden ID:</strong> {order.id}</Text>
                  <Text fontSize="sm"><strong>Producto:</strong> {order.product?.name}</Text>
                  <Text fontSize="sm"><strong>Referencia Bonox:</strong> {order.product?.referenceId}</Text>
                </VStack>
                <Text fontSize="sm" color="blue.300">
                  Si la compra es exitosa, se completará la orden y se enviará el email de confirmación al usuario.
                </Text>
              </VStack>
            </AlertDialogBody>

            <AlertDialogFooter>
              <Button bgColor={'#E53E3E'} ref={cancelRef} onClick={onRetryClose}>
                Cancelar
              </Button>
              <Button
                colorScheme="blue"
                onClick={handleRetryBonox}
                ml={3}
                isLoading={isRetrying}
                loadingText="Reintentando..."
              >
                Reintentar Compra
              </Button>
            </AlertDialogFooter>
          </AlertDialogContent>
        </AlertDialogOverlay>
      </AlertDialog>
    </>
  );
};

const OrdersList = () => {
  const { orders, all, isLoading, count, getStatusColor, getStatusText } = useOrders();
  const [searchParams, setSearchParams] = useSearchParams();

  const [filter, setFilter] = useState<OrdersFilter>({
    user_name: searchParams.get('user_name') || '',
    status: searchParams.get('status') || '',
    page: Number(searchParams.get('page') || 1),
    limit: Number(searchParams.get('limit') || 10)
  });

  const onFilter = ({
    name,
    value
  }: EventTarget & (HTMLInputElement | HTMLSelectElement)) => {
    setFilter({ ...filter, [name]: value })

    if (value) searchParams.set(name, value)
    else searchParams.delete(name)
    setSearchParams(searchParams)
    window.location.hash = "ordenes"
  }

  const refreshOrders = () => {
    all(filter);
  };

  const { page, limit, user_name, status } = filter;

  useEffect(() => {
    all(filter);
  }, [filter]);

  const formatDate = (dateString: Date) => {
    if (!dateString) return '-';
    const date = new Date(dateString);
    return date.toLocaleDateString('es-ES', {
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  const formatAmount = (amount: string) => {
    if (!amount) return '-';
    return `$${amount} USDC`;
  };

  return (
    <Flex gap={5} direction="column" mt={4}>
      <Flex flexDirection={'row'} justify="space-between" pr={6} gap={4}>
        <Flex flex="1" gap={4}>
          <InputGroup w="40%">
            <Input
              color={"white"}
              placeholder="Buscar por usuario (nombre o email)"
              name="user_name"
              value={user_name}
              onChange={(e) => onFilter(e.target)}
            />
            <InputRightElement children={<BsSearch color="white" />} />
          </InputGroup>

          <Select
            color="white"
            placeholder="Filtrar por estado"
            name="status"
            value={status}
            onChange={(e) => onFilter(e.target)}
            w="25%"
          >
            <option value="pending" style={{ backgroundColor: '#2D3748' }}>Pendiente</option>
            <option value="success" style={{ backgroundColor: '#2D3748' }}>Completada</option>
            <option value="error" style={{ backgroundColor: '#2D3748' }}>Error</option>
            <option value="for_revision" style={{ backgroundColor: '#2D3748' }}>En Revisión</option>
            <option value="refunded" style={{ backgroundColor: '#2D3748' }}>Reembolsada</option>
          </Select>
        </Flex>

        <Text color="white" alignSelf="center" fontSize="sm">
          Total: {count} órdenes
        </Text>
      </Flex>

      <Flex w='full' justifyContent='center' px={'20px'} pb={'15px'}>
        <TableContainer overflowX={'scroll'} overflowY={'scroll'} w="100%" maxH="70vh">
          <ChakraTable
            variant="striped"
            size="sm"
            colorScheme="olaggPurple"
            fontSize="14px"
            position='relative'
          >
            <Thead>
              <Tr position='sticky' bg='gray.800' top='0' zIndex='100'>
                <Th py={'20px'} color="white">ID</Th>
                <Th py={'20px'} color="white">Usuario</Th>
                <Th py={'20px'} color="white">Producto</Th>
                <Th py={'20px'} color="white">Inventario</Th>
                <Th py={'20px'} color="white">Monto</Th>
                <Th py={'20px'} color="white">Estado</Th>
                <Th py={'20px'} color="white">Origen</Th>
                <Th py={'20px'} color="white">Fecha</Th>
                <Th py={'20px'} color="white">Acciones</Th>
              </Tr>
            </Thead>
            <Tbody>
              {orders.map((order, index) => (
                <Tr key={index} color="white">
                  <Td py={'15px'} fontSize="12px">{order.id}</Td>
                  <Td py={'15px'}>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm" fontWeight="500">
                        {order.user?.name || 'Sin nombre'}
                      </Text>
                      <Text fontSize="xs" color="gray.400">
                        {order.user?.email}
                      </Text>
                    </VStack>
                  </Td>
                  <Td py={'15px'}>
                    <VStack align="start" spacing={0}>
                      <Text fontSize="sm">{order.product?.name}</Text>
                      {order.claimType && (
                        <Text fontSize="xs" color="green.300">
                          Tipo: {order.claimType}
                        </Text>
                      )}
                    </VStack>
                  </Td>
                  <Td py={'15px'}>
                    <Badge
                      bg={order?.inventorySource === 'OLAGG' ? '#319795' : '#DD6B20'}
                      color="white"
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {order?.inventorySource || '-'}
                    </Badge>
                  </Td>
                  <Td py={'15px'}>{formatAmount(order.amount)}</Td>
                  <Td py={'15px'}>
                    <Badge
                      colorScheme="gray"
                      bg={getStatusColor(order.status)}
                      color="white"
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {getStatusText(order.status)}
                    </Badge>
                  </Td>
                  <Td py={'15px'}>
                    <Badge
                      bg={
                        order?.origin === 'lemon' ? 'green.500' :
                        order?.origin === 'world' ? 'blue.500' :
                        order?.origin === 'web' ? 'purple.500' :
                        'gray.500'
                      }
                      color="white"
                      fontSize="xs"
                      px={2}
                      py={1}
                      borderRadius="md"
                    >
                      {order?.origin || '-'}
                    </Badge>
                  </Td>
                  <Td py={'15px'} fontSize="12px">
                    {formatDate(order.createdAt)}
                  </Td>
                  <Td py={'15px'}>
                    <Actions order={order} onRefresh={refreshOrders} />
                  </Td>
                </Tr>
              ))}
            </Tbody>
          </ChakraTable>
        </TableContainer>
      </Flex>

      <Flex px={'20px'} w='full' justifyContent={'center'}>
        <Pagination
          params={{ limit, page, count }}
          onClick={(newPage) =>
            onFilter({
              name: 'page',
              value: String(newPage + 1)
            } as EventTarget & HTMLInputElement)
          }
          onLimitChange={(newLimit) =>
            onFilter({
              name: 'limit',
              value: newLimit
            } as EventTarget & HTMLInputElement)
          }
          hideLimitInput
        />
      </Flex>
    </Flex>
  )
}

export default OrdersList;